import React from 'react';
import { Card, Col, Empty, Progress, Row, Space, Spin, Statistic, Table, Tag, Typography } from 'antd';
import { FormattedMessage } from 'react-intl';
import { useAuth } from '../../../hooks/useAuth';
import { useGetCohortComparisonQuery } from '../../../services/nestjs-base-api';

const { Text } = Typography;

interface CohortComparisonProps {
    brandId: number | null | undefined;
    period?: string;
    dateFrom?: string;
    dateTo?: string;
}

interface ElementRow {
    code: string;
    name: string;
    panelScore: number | null;
    customerScore: number | null;
    delta: number | null;
}

const fmtScore = (v: number | null | undefined): string =>
    v === null || v === undefined ? '—' : v.toFixed(1);

export const CohortComparison: React.FC<CohortComparisonProps> = ({
    brandId,
    period,
    dateFrom,
    dateTo,
}) => {
    const { user } = useAuth();
    const { data, isLoading } = useGetCohortComparisonQuery(
        {
            jwtToken: user?.jwtToken || '',
            brandId: brandId as number,
            period,
            dateFrom,
            dateTo,
        },
        { skip: !brandId || !user?.jwtToken },
    );

    if (!brandId) {
        return null;
    }
    if (isLoading) {
        return (
            <Card>
                <Spin />
            </Card>
        );
    }
    if (!data) {
        return (
            <Card>
                <Empty
                    description={
                        <FormattedMessage id="responseIntegrity.cohort.empty" />
                    }
                />
            </Card>
        );
    }

    const panel = data.panelCohort;
    const customer = data.customerListCohort;
    const both = !!data.bothCohortsPresent;

    if (!both) {
        const presentName =
            (panel?.responseCount ?? 0) > 0
                ? 'panel'
                : (customer?.responseCount ?? 0) > 0
                  ? 'customer_list'
                  : null;
        return (
            <Card
                title={<FormattedMessage id="responseIntegrity.cohort.title" />}
            >
                <Empty
                    description={
                        presentName ? (
                            <FormattedMessage
                                id="responseIntegrity.cohort.singleCohort"
                                values={{ cohort: presentName }}
                            />
                        ) : (
                            <FormattedMessage id="responseIntegrity.cohort.empty" />
                        )
                    }
                />
            </Card>
        );
    }

    // Merge element scores by code so we can render side-by-side rows.
    const elementsByCode = new Map<string, ElementRow>();
    for (const e of panel?.elements ?? []) {
        elementsByCode.set(e.code, {
            code: e.code,
            name: e.name,
            panelScore: e.score,
            customerScore: null,
            delta: null,
        });
    }
    for (const e of customer?.elements ?? []) {
        const existing = elementsByCode.get(e.code);
        if (existing) {
            existing.customerScore = e.score;
        } else {
            elementsByCode.set(e.code, {
                code: e.code,
                name: e.name,
                panelScore: null,
                customerScore: e.score,
                delta: null,
            });
        }
    }
    const elementRows = Array.from(elementsByCode.values()).map((row) => ({
        ...row,
        delta:
            row.panelScore !== null && row.customerScore !== null
                ? row.customerScore - row.panelScore
                : null,
    }));

    return (
        <Card
            title={<FormattedMessage id="responseIntegrity.cohort.title" />}
        >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                        <Card size="small">
                            <Statistic
                                title={
                                    <FormattedMessage id="responseIntegrity.cohort.panelLabel" />
                                }
                                value={fmtScore(panel?.overallScore ?? null)}
                                suffix={
                                    <span style={{ fontSize: 12, color: '#999' }}>
                                        {' / 100'}
                                    </span>
                                }
                            />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <FormattedMessage
                                    id="responseIntegrity.cohort.responseCount"
                                    values={{ count: panel?.responseCount ?? 0 }}
                                />
                            </Text>
                        </Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card size="small">
                            <Statistic
                                title={
                                    <FormattedMessage id="responseIntegrity.cohort.customerListLabel" />
                                }
                                value={fmtScore(customer?.overallScore ?? null)}
                                suffix={
                                    <span style={{ fontSize: 12, color: '#999' }}>
                                        {' / 100'}
                                    </span>
                                }
                            />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <FormattedMessage
                                    id="responseIntegrity.cohort.responseCount"
                                    values={{ count: customer?.responseCount ?? 0 }}
                                />
                            </Text>
                        </Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card size="small">
                            <Statistic
                                title={
                                    <FormattedMessage id="responseIntegrity.cohort.deltaLabel" />
                                }
                                value={
                                    panel?.overallScore !== null &&
                                    panel?.overallScore !== undefined &&
                                    customer?.overallScore !== null &&
                                    customer?.overallScore !== undefined
                                        ? `${customer.overallScore - panel.overallScore > 0 ? '+' : ''}${(customer.overallScore - panel.overallScore).toFixed(2)}`
                                        : '—'
                                }
                            />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <FormattedMessage id="responseIntegrity.cohort.deltaHint" />
                            </Text>
                        </Card>
                    </Col>
                </Row>

                <Table<ElementRow>
                    rowKey="code"
                    dataSource={elementRows}
                    pagination={false}
                    size="small"
                    columns={[
                        {
                            title: <FormattedMessage id="responseIntegrity.cohort.element" />,
                            dataIndex: 'name',
                            render: (name: string) => <Text strong>{name}</Text>,
                        },
                        {
                            title: <FormattedMessage id="responseIntegrity.cohort.panelScore" />,
                            dataIndex: 'panelScore',
                            width: 200,
                            render: (v: number | null) =>
                                v === null ? (
                                    <Text type="secondary">—</Text>
                                ) : (
                                    <Space size={8}>
                                        <Text strong>{v.toFixed(1)}</Text>
                                        <Progress
                                            percent={Math.max(0, Math.min(100, v))}
                                            size="small"
                                            showInfo={false}
                                            strokeColor="#1890ff"
                                            style={{ width: 100, margin: 0 }}
                                        />
                                    </Space>
                                ),
                        },
                        {
                            title: <FormattedMessage id="responseIntegrity.cohort.customerScore" />,
                            dataIndex: 'customerScore',
                            width: 200,
                            render: (v: number | null) =>
                                v === null ? (
                                    <Text type="secondary">—</Text>
                                ) : (
                                    <Space size={8}>
                                        <Text strong>{v.toFixed(1)}</Text>
                                        <Progress
                                            percent={Math.max(0, Math.min(100, v))}
                                            size="small"
                                            showInfo={false}
                                            strokeColor="#52c41a"
                                            style={{ width: 100, margin: 0 }}
                                        />
                                    </Space>
                                ),
                        },
                        {
                            title: <FormattedMessage id="responseIntegrity.cohort.delta" />,
                            dataIndex: 'delta',
                            width: 90,
                            align: 'right',
                            render: (v: number | null) => {
                                if (v === null) return <Text type="secondary">—</Text>;
                                const color =
                                    v > 0 ? '#52c41a' : v < 0 ? '#fa541c' : undefined;
                                return (
                                    <Tag color={color ? undefined : 'default'} style={{ color, margin: 0 }}>
                                        {v > 0 ? '+' : ''}
                                        {v.toFixed(2)}
                                    </Tag>
                                );
                            },
                        },
                    ]}
                />
            </Space>
        </Card>
    );
};
